import React from 'react';
import { NumberRow } from '../chancey';
import type { ScannedTicket } from '../../hooks/scannedTicketUtils';
import type { ThemePalette, TypographyTokens } from '../../theme/tokens';
import { statusBg, statusFg, statusLabel } from '../../utils/ticketStatus';

export interface TicketSnapshotCardProps {
  ticket: ScannedTicket;
  c: ThemePalette;
  typ: TypographyTokens;
  variant?: 'carousel' | 'list';
  onOpen: (ticket: ScannedTicket, trigger: HTMLElement) => void;
}

export const TicketSnapshotCard: React.FC<TicketSnapshotCardProps> = ({
  ticket,
  c,
  typ,
  variant = 'carousel',
  onOpen,
}) => {
  const match = winningMatchForTicket(ticket);
  const list = variant === 'list';

  return (
    <button
      type="button"
      onClick={(event) => onOpen(ticket, event.currentTarget)}
      aria-label={`Ticket #${ticket.id.slice(0, 6).toUpperCase()}, ${statusLabel(ticket.status, ticket.prize)}. Open details.`}
      className={[
        list ? 'w-full' : 'min-w-[240px] shrink-0',
        'cursor-pointer rounded-[18px] border p-3.5 text-left transition-transform active:scale-[0.98]',
      ].join(' ')}
      style={{ background: c.surface, borderColor: c.line }}
    >
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div
          className="font-mono text-[11px] tracking-[0.1em]"
          style={{ color: c.inkMuted, fontFamily: typ.mono }}
        >
          #{ticket.id.slice(0, 6).toUpperCase()}
        </div>
        <div
          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em]"
          style={{
            background: statusBg(ticket.status, c),
            color: statusFg(ticket.status, c),
            fontFamily: typ.body,
          }}
        >
          {statusLabel(ticket.status, ticket.prize)}
        </div>
      </div>
      <NumberRow
        main={ticket.main}
        bonus={ticket.bonus}
        matchedMain={match?.main}
        matchedBonus={match?.bonus}
        size={list ? 30 : 26}
        gap={5}
        c={c}
      />
      <div
        className="mt-2.5 flex items-center justify-between gap-3 text-[11px]"
        style={{ color: c.inkSoft, fontFamily: typ.body }}
      >
        <span>Draw {ticket.drawDate ?? 'unknown'}</span>
        {list ? <span>{formatScannedAt(ticket.scannedAt)}</span> : null}
      </div>
    </button>
  );
};

function winningMatchForTicket(ticket: ScannedTicket): ScannedTicket['match'] | undefined {
  if (ticket.status !== 'jackpot' && ticket.status !== 'won-small') return undefined;
  return ticket.match;
}

function formatScannedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Saved';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
